import { t } from '@/i18n';
import type { ApiError, BackendErrorPayload } from '@chikara/types';
import apiErrors from './apiErrors.json';

function asBackendPayload(source: unknown): BackendErrorPayload | undefined {
  if (!source || typeof source !== 'object') return undefined;
  const payload = source as Record<string, unknown>;
  const error = payload.error;
  if (!error || typeof error !== 'object') return undefined;

  const err = error as Record<string, unknown>;
  const meta = payload.meta;
  const metaObj =
    meta && typeof meta === 'object'
      ? (meta as Record<string, unknown>)
      : undefined;

  return {
    response: payload.response === 'OK' ? 'OK' : 'BAD',
    error: {
      code: String(err.code ?? ''),
      message: String(err.message ?? ''),
      trace_id: String(err.trace_id ?? ''),
    },
    meta: {
      request_id: String(metaObj?.request_id ?? ''),
      timestamp: String(metaObj?.timestamp ?? ''),
    },
  };
}

/**
 * Intenta parsear un string como un error en formato JSON y extraer su código y mensaje.
 */
function tryParseJsonError(str: string): { code?: string; message?: string } | null {
  if (typeof str !== 'string' || !str.trim().startsWith('{')) {
    return null;
  }
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === 'object') {
      if (parsed.error && typeof parsed.error === 'object') {
        return {
          code: parsed.error.code,
          message: parsed.error.message
        };
      }
      if (parsed.response === 'BAD' && parsed.data?.exception) {
        return {
          code: parsed.data.exception.code,
          message: parsed.data.exception.detail
        };
      }
      if (parsed.data?.exception) {
        return {
          code: parsed.data.exception.code,
          message: parsed.data.exception.detail
        };
      }
    }
  } catch {
    // Ignorar error de parseo
  }
  return null;
}

/**
 * Obtiene el mensaje personalizado para un código de error específico desde el JSON de diccionario.
 * Si no tiene una entrada configurada, usa el mensaje de fallback provisto.
 */
function getCustomErrorMessage(code: string | undefined, fallbackMessage: string): string {
  if (!code) return fallbackMessage;

  const errorEntry = (apiErrors as Record<string, { code: string; message: string }>)[code];
  if (errorEntry && errorEntry.message) {
    let { message } = errorEntry;

    if (message.includes('{{email}}')) {
      const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
      const match = fallbackMessage.match(emailRegex);
      if (match) {
        message = message.replace('{{email}}', match[0]);
      }
    }

    return message;
  }

  return fallbackMessage;
}

export function mapApiError(error: any): ApiError {
  let activeError = error;

  // 1. Si el error recibido es un string, intentamos parsearlo
  if (typeof error === 'string') {
    try {
      activeError = JSON.parse(error);
    } catch {
      return {
        type: 'network',
        status: 500,
        message: error || t('errors.unknown'),
      };
    }
  }

  if (activeError?.error) {
    const backendError = activeError.error;
    let errorCode = backendError.code;
    let errorMessage = backendError.message;

    // 2. Si el mensaje es a su vez un string de JSON (error anidado stringificado), lo parseamos
    const nested = tryParseJsonError(backendError.message);
    if (nested) {
      if (nested.code) errorCode = nested.code;
      if (nested.message) errorMessage = nested.message;
    }

    const validationMessages =
      backendError.details?.validation?.map(
        (v: any) => v.message
      ) ?? [];

    if (errorCode === '007.005.422' && errorMessage === 'password_already_used') {
      errorCode = 'PASSWORD_ALREADY_USED';
    }

    const baseMessage = getCustomErrorMessage(
      errorCode,
      errorMessage || t('errors.unknown')
    );

    const message = [
      baseMessage,
      ...validationMessages,
    ].join('\n');

    const status = (activeError.response && typeof activeError.response === 'object')
      ? activeError.response.status
      : 400;

    return {
      type: 'http',
      status: status ?? 400,
      code: errorCode,
      message,
      backendMessage: errorMessage,
      traceId: backendError.trace_id,
      backend: asBackendPayload(activeError),
      timestamp: activeError.meta?.timestamp,
    };
  }

  if (activeError?.response === 'BAD' && activeError?.data?.exception) {
    const rawCode = String(activeError.data.exception.code || '');
    const rawDetail = String(activeError.data.exception.detail || '');

    let finalCode = rawCode;
    let finalMessage = rawDetail;

    const hasSpaces = (str: string) => str.includes(' ');
    const isCodeLike = (str: string) => str.length > 0 && !hasSpaces(str);

    if (
      (hasSpaces(rawCode) && isCodeLike(rawDetail)) ||
      (/^[\d.]+$/.test(rawDetail) && !/^[\d.]+$/.test(rawCode))
    ) {
      finalCode = rawDetail;
      finalMessage = rawCode;
    }

    // 3. Si finalMessage es a su vez un string de JSON, lo parseamos
    const nested = tryParseJsonError(finalMessage);
    if (nested) {
      if (nested.code) finalCode = nested.code;
      if (nested.message) finalMessage = nested.message;
    }

    const message = getCustomErrorMessage(finalCode, finalMessage || t('errors.unknown'));

    return {
      type: 'http',
      status: 400,
      code: finalCode,
      message,
      backendMessage: finalMessage,
      backend: asBackendPayload({
        response: 'BAD',
        error: {
          code: finalCode,
          message: finalMessage,
          trace_id: '',
        },
        meta: activeError.meta,
      }),
      timestamp: activeError.meta?.timestamp,
    };
  }

  // 4. Si el objeto no calza pero tiene campos que parezcan JSON o es un objeto genérico
  // que contiene data.exception o response === 'BAD'
  if (activeError && typeof activeError === 'object') {
    if (activeError.data?.exception) {
      const code = activeError.data.exception.code;
      const detail = activeError.data.exception.detail;
      const message = getCustomErrorMessage(code, detail || t('errors.unknown'));
      return {
        type: 'http',
        status: 400,
        code,
        message,
        backendMessage: detail,
        backend: asBackendPayload({
          response: 'BAD',
          error: { code, message: detail, trace_id: '' },
          meta: activeError.meta,
        }),
        timestamp: activeError.meta?.timestamp,
      };
    }
  }

  return {
    type: 'network',
    status: 500,
    message: t('errors.unknown'),
  };
}

